import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

import { VexSidebarComponent } from '@vex/components/vex-sidebar/vex-sidebar.component';

import { toSignal } from '@angular/core/rxjs-interop';
import {
  MatError,
  MatFormField,
  MatLabel,
  MatPrefix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { BaseComponent } from '@shared/components/base/base-component/base.component';
import { CriterioModel } from '@shared/criterio';
import { VendaCampos } from '@shared/venda/ui';
import {
  DashboardCardTipo,
  DashboardCardTipoType,
} from '../../models/dashboard.model';
import { DashboardService } from '../../services/dashboard.service';

type CriterioDadoAdicionalConfig = CriterioModel & {
  dadosAdicionaisSel: FormControl;
};

@Component({
    selector: 'app-dashboard-criar-card',
    imports: [
        MatButton,
        MatError,
        MatFormField,
        MatIcon,
        MatInput,
        MatLabel,
        MatOption,
        MatPrefix,
        MatSelect,
        ReactiveFormsModule,
    ],
    changeDetection: ChangeDetectionStrategy.Default,
    templateUrl: './dashboard-criar-card.component.html',
    styleUrls: ['./dashboard-criar-card.component.scss'],
    host: {
        tabindex: '0',
    }
})
export class DashboardCriarCardComponent extends BaseComponent {
  // Input
  sidebarComponent = input<VexSidebarComponent>();

  // Injectable
  private cdr = inject(ChangeDetectorRef);
  private dashboardSvc = inject(DashboardService);
  private fb = inject(FormBuilder);

  // Atributos private
  private criterios = toSignal(this.dashboardSvc.getCriterios());

  // Atributos protected
  protected dashboardConfig = toSignal(this.dashboardSvc.getDashboardConfig());
  protected dimensoes = computed(() => {
    const dashboardConfig = this.dashboardConfig();
    return this.buildLista(dashboardConfig?.dimensoes);
  });
  protected form = this.buildForm();
  protected iconsTipo = this.buildIconsTipo();
  protected readonly tipoCard = DashboardCardTipo;
  protected valores = computed(() => {
    const dashboardConfig = this.dashboardConfig();
    return this.buildLista(dashboardConfig?.valores);
  });

  // Métodos protected
  protected onCriar() {
    const formValue = this.form.value;
    const dimensoesForm = (Array.isArray(formValue.dimensoes)
      ? formValue.dimensoes
      : [formValue.dimensoes]) as any as CriterioDadoAdicionalConfig[];
    const dimensoesConv = dimensoesForm.map((dim) => ({
      id: dim.id!,
      tipoDado: dim.tipoDado,
      dadosAdicionais: (dim.dadosAdicionaisSel?.value || []) as string[],
    }));
    this.dashboardSvc
      .createCard({
        titulo: formValue.titulo!,
        tipo: formValue.tipo!,
        valores: formValue.valores as any as string[],
        dimensoes: dimensoesConv,
      })
      .subscribe({
        next: (novoCard) => {
          this.openSnackbar('Card criado com sucesso');
          this.sidebarComponent()?.close();
          this.cdr.markForCheck();
        },
      });
  }

  protected getCriteriosDadosAdicionais() {
    const dimensoesValue = this.form.controls.dimensoes.value;
    if (!dimensoesValue) {
      return [];
    }

    const dimensoes = (Array.isArray(dimensoesValue)
      ? dimensoesValue
      : [dimensoesValue]) as any as CriterioDadoAdicionalConfig[];

    return dimensoes
      .filter((dim) => dim.dadosAdicionais && dim.dadosAdicionais.length)
      .map((dim) => {
        if (!dim.dadosAdicionaisSel) {
          dim.dadosAdicionaisSel = this.fb.control([]);
        }
        return dim;
      });
  }

  // Métodos privados
  private buildLista(lista?: string[]) {
    if (!lista) {
      return [];
    }

    const criterios = this.criterios();
    return lista
      .map((item) => {
        if (
          item.startsWith('infoVenda.') ||
          item.startsWith('infoVenda.itens.')
        ) {
          const itemCriterioId = item.replace(/^.*\./, '');
          const criterio = criterios?.find(
            (crit) => crit.nomeTecnico === itemCriterioId
          );
          return {
            id: item,
            nome: criterio?.nome || null,
            tipoDado: criterio?.tipoDado,
            dadosAdicionais: criterio?.dadosAdicionais,
          };
        }

        const vendaCampo = VendaCampos[item];
        if (vendaCampo) {
          return {
            id: item,
            nome: vendaCampo.descricao,
            tipoDado: vendaCampo.tipo,
          };
        }

        return {
          id: item,
          nome: null,
        };
      })
      .filter((item) => item.nome)
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }

  private buildForm() {
    return this.fb.nonNullable.group({
      id: '',
      titulo: ['', [Validators.required]],
      tipo: DashboardCardTipo.LISTA as DashboardCardTipoType,
      dimensoes: [[], Validators.required],
      valores: [[], Validators.required],
    });
  }

  private buildIconsTipo() {
    return {
      [DashboardCardTipo.LISTA]: 'mat:format_list_bulleted',
      [DashboardCardTipo.TOTALIZADOR]: 'mat:functions',
      [DashboardCardTipo.GRAFICO_COLUNA]: 'mat:bar_chart',
      [DashboardCardTipo.GRAFICO_LINHA]: 'mat:show_chart',
      [DashboardCardTipo.GRAFICO_PIZZA]: 'mat:pie_chart',
    };
  }
}
